/**
 * RC Softphone — in-app dialer backed by the RC Embeddable widget (iframe).
 *
 * Why an iframe?
 *   The RC Embeddable Widget is a full WebRTC softphone served by RingCentral.
 *   We reuse it directly so the CRM gets real audio/WebRTC with zero SIP
 *   plumbing on our side. The parent (CRM) tracks lightweight UI state via
 *   window `postMessage` events emitted by the widget.
 *
 * UX contract (Softphone-first):
 *   • Default mode on every mount = "softphone" for real users. Session only
 *     flips to "ringout" when softphone fails (iframe timeout or mic denial)
 *     or when the user explicitly opts into the fallback panel.
 *   • We do NOT persist mode across sessions — old localStorage keys from
 *     earlier versions are actively cleared to prevent "RingOut sticky" regressions.
 *   • RingOut controls stay hidden behind a "Having audio issues?" disclosure link;
 *     the panel expands in place so the call action feels continuous.
 *
 * Fallback:
 *   Iframe fails to load within `IFRAME_TIMEOUT_MS` OR mic permission denied →
 *   auto-switch to ringout fallback, reveal the panel, emit a toast. Users can
 *   retry softphone from the panel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Phone, PhoneCall, PhoneOff, Mic, MicOff, AlertTriangle, RotateCcw } from "lucide-react";
import { makeRCCall, pickRCErrorMessage } from "@/lib/rcIntegrationApi";

const EMBED_URL = "https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/app.html";
const LEGACY_MODE_KEY = "crm.dialer.mode"; // kept only to clear forward
const IFRAME_TIMEOUT_MS = 7000;
// Height contract — the RC Embeddable widget needs roughly 520-560px of
// vertical space to render the keypad AND the primary call button without
// inner scrolling. Using a viewport-aware height keeps the call CTA visible
// on common laptop heights (768/800/900) while still capping growth on
// large monitors. The min/max bracket prevents regressions when the parent
// page is unusually short or tall.
const SOFTPHONE_IFRAME_HEIGHT = "min(72vh, 620px)";
const SOFTPHONE_IFRAME_MIN_HEIGHT = 520;

/**
 * Mode is runtime-only state now (no cross-session persistence).
 * We migrate/ignore any legacy storage key silently on mount so previous
 * "ringout default" users get Softphone back.
 */
function clearLegacyModeStorage() {
  try { localStorage.removeItem(LEGACY_MODE_KEY); } catch {}
  try { sessionStorage?.removeItem?.(LEGACY_MODE_KEY); } catch {}
}

function StateBadge({ state }) {
  const map = {
    idle:      { label: "Idle",       color: "var(--text-tertiary)", bg: "var(--gray-light)" },
    dialing:   { label: "Dialing…",   color: "#b07400",              bg: "#fff4d6" },
    ringing:   { label: "Ringing…",   color: "#b07400",              bg: "#fff4d6" },
    connected: { label: "Connected",  color: "var(--green)",         bg: "var(--green-light)" },
    ended:     { label: "Ended",      color: "var(--text-secondary)", bg: "var(--gray-light)" },
    failed:    { label: "Failed",     color: "var(--red)",           bg: "var(--red-light)" },
  };
  const s = map[state] || map.idle;
  return (
    <span data-testid={`softphone-state-${state}`}
          style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
                   background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

/**
 * Request microphone permission *before* the user tries to place a call so the
 * browser prompt is not buried inside an iframe interaction.
 */
async function requestMic() {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return true;
  } catch { return false; }
}

/**
 * Main softphone component. Used in both Communications and Sales Dialer.
 * Props:
 *  - prefillNumber: optional number to pre-populate the ringout fallback input
 *  - onStateChange(state): optional callback so parent can mirror call state
 */
export default function RCSoftphone({ prefillNumber = "", onStateChange }) {
  // Single source of truth for mode — runtime-only state, no persistence.
  // `fallbackRevealed` independently controls whether the RingOut panel is
  // user-visible. On successful softphone mount the panel stays hidden behind
  // the "Having audio issues?" disclosure.
  const [mode, setMode] = useState("softphone");
  const [fallbackRevealed, setFallbackRevealed] = useState(false);
  const [softphoneFailed, setSoftphoneFailed] = useState(false);
  const [callState, setCallState] = useState("idle");
  const [number, setNumber] = useState(prefillNumber || "");
  const [timer, setTimer] = useState(0);
  const [muted, setMuted] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeFailed, setIframeFailed] = useState(false);
  const [micGranted, setMicGranted] = useState(null);
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);

  // Migrate: clear any legacy storage key so it can never restore ringout-by-default.
  useEffect(() => { clearLegacyModeStorage(); }, []);

  // Keep parent in sync with call state.
  useEffect(() => { onStateChange?.(callState); }, [callState, onStateChange]);

  // Prefill sync (e.g., clicking a recent call).
  useEffect(() => { if (prefillNumber) setNumber(prefillNumber); }, [prefillNumber]);

  // Call timer — ticks only while connected.
  useEffect(() => {
    if (callState !== "connected") return;
    const i = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(i);
  }, [callState]);

  /** Centralized softphone-failure handler: auto-switch to RingOut, reveal the
   *  fallback panel, and emit a single clear toast. */
  const triggerFallback = useCallback((reason) => {
    setSoftphoneFailed(true);
    setMode("ringout");
    setFallbackRevealed(true);
    toast.error("Softphone unavailable. Switched to RingOut fallback.");
    // `reason` is stored in component state so the fallback panel can show a hint.
    if (reason === "iframe") setIframeFailed(true);
  }, []);

  /** User-initiated retry: reset the failure flags and attempt softphone again. */
  const retrySoftphone = useCallback(() => {
    setSoftphoneFailed(false);
    setIframeFailed(false);
    setIframeReady(false);
    setMicGranted(null);
    setFallbackRevealed(false);
    setMode("softphone");
  }, []);

  // Iframe load watchdog: if we don't see the load event within timeout, fall back.
  useEffect(() => {
    if (mode !== "softphone" || iframeReady || iframeFailed) return;
    timeoutRef.current = setTimeout(() => {
      if (!iframeReady) triggerFallback("iframe");
    }, IFRAME_TIMEOUT_MS);
    return () => clearTimeout(timeoutRef.current);
  }, [mode, iframeReady, iframeFailed, triggerFallback]);

  // Ask for mic permission when entering softphone mode.
  useEffect(() => {
    if (mode !== "softphone" || micGranted !== null) return;
    requestMic().then((ok) => {
      setMicGranted(ok);
      if (!ok) triggerFallback("mic");
    });
  }, [mode, micGranted, triggerFallback]);

  /**
   * Listen to RC Embeddable events.
   * The widget emits `{type: 'rc-call-ring-notify' | 'rc-call-init-notify' |
   * 'rc-call-start-notify' | 'rc-call-end-notify' | 'rc-login-status-notify'}`.
   * We translate into our 6-state machine.
   */
  useEffect(() => {
    function onMsg(ev) {
      if (!ev?.data || typeof ev.data !== "object") return;
      const t = ev.data.type;
      if (t === "rc-call-init-notify") { setCallState("dialing"); setTimer(0); }
      else if (t === "rc-call-ring-notify") { setCallState("ringing"); }
      else if (t === "rc-call-start-notify") { setCallState("connected"); setTimer(0); }
      else if (t === "rc-call-end-notify") {
        const reason = ev.data?.call?.result || ev.data?.call?.reason;
        setCallState(reason && reason.toLowerCase().includes("fail") ? "failed" : "ended");
      }
      else if (t === "rc-login-status-notify") {
        // Widget logged in / out — treat as iframe_ready signal.
        setIframeReady(true);
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // ── Ringout fallback: call via REST ──
  const placeRingOut = async () => {
    if (!number) return;
    setCallState("dialing");
    try {
      const res = await makeRCCall({ to: number });
      if (res === null) { setCallState("idle"); return; }    // demo-blocked path
      setCallState("connected");   // RingOut succeeded; call is on the user's phone
    } catch (e) {
      setCallState("failed");
      toast.error(pickRCErrorMessage(e, "Call failed"));
    }
  };

  const hangup = () => {
    setCallState("ended");
    setTimer(0);
    // Try to tell the widget to hang up via postMessage — no-op if not supported.
    try {
      iframeRef.current?.contentWindow?.postMessage({ type: "rc-adapter-hangup" }, "*");
    } catch {}
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: next ? "rc-adapter-mute" : "rc-adapter-unmute" }, "*",
      );
    } catch {}
  };

  const mmss = useMemo(() => {
    const m = Math.floor(timer / 60);
    const s = (timer % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  }, [timer]);

  const showSoftphone = mode === "softphone" && !softphoneFailed;
  const showFallback = fallbackRevealed || softphoneFailed;

  return (
    <div data-testid="rc-softphone" style={{ border: "0.5px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
      {/* Header — single source of truth for call state */}
      <div style={{ display: "flex", alignItems: "center", padding: "8px 12px", borderBottom: "0.5px solid var(--border)", gap: 8 }}>
        <Phone style={{ width: 12, height: 12, color: "var(--blue)" }} />
        <span style={{ fontSize: 12, fontWeight: 650 }}>RingCentral Softphone</span>
        <span style={{ marginLeft: "auto" }} data-testid="softphone-header-state">
          <StateBadge state={callState} />
        </span>
      </div>

      {/* Pinned quick-call row — always visible above the iframe so the
          primary CTA never falls below the fold even if the embed itself
          renders short. Mirrors `number` from the fallback panel so users
          can place a call without expanding the disclosure.
          Note: this row is gated by `showSoftphone`. When the RC iframe
          fails (mic denied / network blocked / CI preview) the component
          flips to the RingOut fallback panel below — that panel's
          green "Ring my phone" button preserves the same above-the-fold
          call CTA so users always have a primary action visible without
          scrolling, regardless of which transport is active. */}
      {showSoftphone && (
        <div data-testid="softphone-quick-call"
             style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderBottom: "0.5px solid var(--border)", background: "#fff" }}>
          <input
            data-testid="softphone-quick-call-input"
            className="input"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+1 555 555 1212"
            disabled={callState === "dialing" || callState === "connected"}
            style={{ flex: 1, fontSize: 12, height: 28, padding: "0 8px" }}
          />
          <button
            type="button"
            data-testid="softphone-quick-call-btn"
            onClick={placeRingOut}
            disabled={!number || callState === "dialing" || callState === "connected"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "var(--green)", color: "#fff", border: "none",
              padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              cursor: (!number || callState === "dialing" || callState === "connected") ? "not-allowed" : "pointer",
              opacity: (!number || callState === "dialing" || callState === "connected") ? 0.55 : 1,
            }}
            title="Place call now (uses RingOut if softphone is still loading)"
          >
            <PhoneCall style={{ width: 11, height: 11 }} /> Call
          </button>
        </div>
      )}

      {/* Softphone body (hidden when softphone has failed) */}
      {showSoftphone && (
        <div style={{ position: "relative" }} data-testid="softphone-body">
          <div style={{ padding: "6px 12px", display: "flex", alignItems: "center", gap: 8, background: "#fafafa", borderBottom: "0.5px solid var(--border)" }}>
            {callState === "connected" && <span data-testid="softphone-timer" style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 600 }}>{mmss}</span>}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button data-testid="softphone-mute" onClick={toggleMute} disabled={callState !== "connected"}
                      style={{ width: 24, height: 24, borderRadius: "50%", border: "0.5px solid var(--border)", background: muted ? "var(--red-light)" : "#fff", cursor: callState === "connected" ? "pointer" : "not-allowed", opacity: callState === "connected" ? 1 : 0.4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {muted ? <MicOff style={{ width: 12, height: 12, color: "var(--red)" }} /> : <Mic style={{ width: 12, height: 12, color: "var(--text-secondary)" }} />}
              </button>
              <button data-testid="softphone-hangup" onClick={hangup} disabled={callState !== "connected" && callState !== "ringing" && callState !== "dialing"}
                      style={{ width: 24, height: 24, borderRadius: "50%", border: "none", background: "var(--red)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: (callState === "connected" || callState === "ringing" || callState === "dialing") ? 1 : 0.35 }}>
                <PhoneOff style={{ width: 12, height: 12, color: "#fff" }} />
              </button>
            </div>
          </div>
          <iframe
            ref={iframeRef}
            src={EMBED_URL}
            title="RingCentral Softphone"
            data-testid="rc-embed-iframe"
            style={{
              width: "100%",
              height: SOFTPHONE_IFRAME_HEIGHT,
              minHeight: SOFTPHONE_IFRAME_MIN_HEIGHT,
              border: "none",
              display: iframeFailed ? "none" : "block",
            }}
            allow="microphone; camera"
            onLoad={() => { setIframeReady(true); clearTimeout(timeoutRef.current); }}
            onError={() => triggerFallback("iframe")}
          />
          {/* Fallback disclosure — hidden by default, revealing the panel inline below */}
          {!fallbackRevealed && (
            <div style={{ padding: "8px 12px", textAlign: "center", borderTop: "0.5px solid var(--border)", background: "#fafafa" }}>
              <button
                type="button"
                data-testid="reveal-fallback"
                onClick={() => setFallbackRevealed(true)}
                style={{ fontSize: 10, color: "var(--text-tertiary)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
              >
                Having audio issues?
              </button>
            </div>
          )}
        </div>
      )}

      {/* Fallback panel — user can retry softphone or place a RingOut call */}
      {showFallback && (
        <div style={{ padding: "12px 16px", borderTop: showSoftphone ? "0.5px solid var(--border)" : "none", background: "#fafafa" }} data-testid="fallback-panel">
          {softphoneFailed ? (
            <div data-testid="fallback-banner" style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 10, fontSize: 11, color: "#a32d2d" }}>
              <AlertTriangle style={{ width: 12, height: 12, marginTop: 2, flexShrink: 0 }} />
              <span>Softphone unavailable. Use RingOut as a temporary fallback or retry Softphone below.</span>
            </div>
          ) : (
            <div style={{ fontSize: 10, color: "var(--text-tertiary)", marginBottom: 10 }}>
              RingOut fallback — places the call on the phone number registered with your RingCentral account.
            </div>
          )}
          <input
            data-testid="dialer-ringout-input"
            className="input"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="+15555551212"
            disabled={callState === "dialing"}
            style={{ width: "100%", marginBottom: 10, fontSize: 14, textAlign: "center" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              data-testid="retry-softphone"
              onClick={retrySoftphone}
              style={{ background: "transparent", color: "var(--text-secondary)", border: "0.5px solid var(--border)", padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
            >
              <RotateCcw style={{ width: 11, height: 11 }} /> Retry Softphone
            </button>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                data-testid="dialer-ringout-call"
                onClick={placeRingOut}
                disabled={!number || callState === "dialing"}
                style={{ background: "var(--green)", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: (!number || callState === "dialing") ? "not-allowed" : "pointer", opacity: (!number || callState === "dialing") ? 0.55 : 1, display: "flex", alignItems: "center", gap: 4 }}
              >
                <PhoneCall style={{ width: 13, height: 13 }} /> Ring my phone
              </button>
              {(callState === "connected" || callState === "dialing") && (
                <button data-testid="dialer-ringout-hangup" onClick={hangup}
                        style={{ background: "var(--red)", color: "#fff", border: "none", padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  End
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
